import { useMemo } from 'react';

import { UTCDate } from '@date-fns/utc';
import { OrderDetailsSchema, OrderSchema } from 'api/types/data';
import { ClientInfoEntity } from 'api/types/entity';
import { OrderOperationType } from 'api/types/entity/account/order';
import { convertDateByTimezone, parseFullName } from 'helpers';
import { useUser } from 'libs/providers';
import { DeepPartial } from 'types/deepPartial';

import { OrderFormType } from '../../common/forms/orderForm';
import { OrderDetailsFormType } from '../../common/forms/orderForm/type';

const parseDetails = (
  detailsSchema: OrderDetailsSchema | undefined,
  client: ClientInfoEntity | null
): Partial<OrderDetailsFormType> => ({
  brokerName: detailsSchema?.brokerName
    ? {
        value: detailsSchema.brokerName.referenceRecordId,
        label: detailsSchema.brokerName.value,
        record: {
          id: detailsSchema.brokerName.referenceRecordId,
        },
      }
    : undefined,
  accountNumber: client?.personalAccountNumber ?? undefined,
  accountOwnerName: client
    ? client?.companyName ?? parseFullName(client)
    : undefined,
});

export const useOrderInitialValues = (
  orderType: OrderOperationType,
  orderSchema: OrderSchema | undefined
) => {
  const { client } = useUser();

  const initialValues = useMemo<DeepPartial<OrderFormType>>(
    () => ({
      senderDetails:
        orderType === OrderOperationType.SecuritiesWithdrawal
          ? parseDetails(orderSchema?.senderDetails, client)
          : undefined,

      recipientDetails:
        orderType === OrderOperationType.SecuritiesDeposit
          ? parseDetails(orderSchema?.recipientDetails, client)
          : undefined,

      startDate: orderSchema?.startDate
        ? convertDateByTimezone(new UTCDate(orderSchema.startDate), 18000)
        : undefined,
    }),
    [client, orderSchema, orderType]
  );

  return initialValues;
};
